import React from 'react'
import type { Maybe } from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import type { primitives } from '@adc/blueprint'
import { Text, Icon, PopupTooltip } from '@adc/ui-adc'
import _cx from 'classnames/bind'
import Header from 'common/modules/bidBanner/header'
import FAQSection from 'common/modules/bidBanner/faqSection'
import {
  Container,
  LeftMarginSection,
} from 'common/modules/bidBanner/container'
import {
  CountdownDuring,
  getEndTimeMoment,
  getServerTimeMoment,
} from 'common/modules/auctionCountdown'
import { AuctionExtensionObserver } from 'common/modules/auctionExtensionObserver'
import BidForm from 'common/modules/bidBanner/bidForm'
import BidFormUI from 'common/modules/bidBanner/bidForm/ui'
import * as BidDetails from 'common/modules/bidBanner/bidDetails'
import { FeatureFlags, useListingContext } from 'common/context/listing'
import interpolate from '../../../utils/interpolate'
import TimeInformation from '../timeInformation'
import { TimeExtensionTooltip } from '../tooltips'
import { formatMoneyWithCentsExceptZero } from '../../../utils/formatters'
import { PresenceContext } from '../../../contexts'
import MatchBidHeader from '../matchBid/matchBidHeader/index'
import ReserveMetRibbon from '../reserveRibbon'
import cls from './styles.scss'
import { BID_BANNER_STATES, getBidBannerState } from './common'
const cx = _cx.bind(cls)

const HEADER_TEXT_OUTBID_OR_COUNTERBID = 'You were outbid.'
const HEADER_TEXT_OUTBID_OR_COUNTERBID_SUBTEXT = 'Bid again.'
const HEADER_TEXTS = {
  STARTED: 'The auction started.',
  STARTED_SUBTEXT: 'Your bid must be %s or higher.',
  HIGHEST_BIDDER_RESERVE_NOT_MET:
    "Your bid is the highest, but the Reserve wasn't met.",
  HIGHEST_BIDDER_RESERVE_NOT_MET_SUBTEXT: 'Do you want to bid again?',
  HIGHEST_BIDDER_RESERVE_MET: 'You are the highest bidder.',
  ONE_AWAY: 'Next bid hits Reserve.',
  TWO_AWAY: 'Reserve Almost Met, Bid Now!',
  OUTBID: HEADER_TEXT_OUTBID_OR_COUNTERBID,
  OUTBID_SUBTEXT: HEADER_TEXT_OUTBID_OR_COUNTERBID_SUBTEXT,
  COUNTERBID: HEADER_TEXT_OUTBID_OR_COUNTERBID,
  COUNTERBID_SUBTEXT: HEADER_TEXT_OUTBID_OR_COUNTERBID_SUBTEXT,
}

interface DuringProps {
  shouldShowOneAwayMessage: boolean
  shouldShowTwoAwayMessage: boolean
  bidDeposit: Maybe<number | undefined>
  experiment: any
  performBidVerificationAction: any
  featureFlags: FeatureFlags
  user: primitives.AuctionUser
}

export const During = ({
  performBidVerificationAction,
  featureFlags,
  shouldShowOneAwayMessage,
  shouldShowTwoAwayMessage,
  user,
  bidDeposit,
}: DuringProps) => {
  const { listing, segment } = useListingContext()
  const renderBidForm = () => {
    return (
      <BidForm
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        performBidVerificationAction={performBidVerificationAction}
        featureFlags={featureFlags}
        shouldShowOneAwayMessage={shouldShowOneAwayMessage}
        listing={listing}
        segment={segment}
        user={user}
      >
        {(props: any) => <BidFormUI {...props} />}
      </BidForm>
    )
  }

  const renderRightRibbon = () => {
    return segment && <ReserveMetRibbon segment={segment} />
  }

  const renderSecond = () => {
    const isMatchBiddingEnabled =
      segment?.configuration?.is_match_bidding_enabled &&
      featureFlags['fe.resi.enable-match-bidding']

    return (
      segment && (
        <>
          <AuctionExtensionObserver segment={segment}>
            {(didBidExtendAuction: boolean) => (
              <>
                {isMatchBiddingEnabled && <MatchBidHeader />}
                <CountdownDuring
                  endTime={getEndTimeMoment(segment)}
                  currentServerTime={getServerTimeMoment(segment)}
                  segment={segment}
                />
                <div className={cx('tooltip-container')}>
                  {didBidExtendAuction && <TimeExtensionTooltip />}
                </div>
              </>
            )}
          </AuctionExtensionObserver>
          {renderBidForm()}
        </>
      )
    )
  }

  const renderBidDetails = () => {
    return (
      <LeftMarginSection>
        <BidDetails.During
          segment={segment}
          listing={listing}
          bidDeposit={bidDeposit}
        />
      </LeftMarginSection>
    )
  }

  const renderTopDuringAuction = () => {
    return (
      <div className={cx('u-mb-5')}>
        <TimeInformation segment={segment} />
      </div>
    )
  }

  const renderNoBids = () => {
    const { nextBidAmount } =
      segment.__methods__.calculateAuctionValues(listing)
    const headerSubText = interpolate(HEADER_TEXTS.STARTED_SUBTEXT, [
      formatMoneyWithCentsExceptZero(nextBidAmount),
    ])
    return (
      <Container
        ribbonColor='adcLightBlue'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='adcLightBlue'
                icon={<Header.IconGavel color='adcLightBlue' />}
                subText={headerSubText}
                text={HEADER_TEXTS.STARTED}
              />
            </div>
            {renderBidDetails()}

            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderHighestBidderReserveMet = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='success'
                icon={<Header.IconSmile />}
                text={HEADER_TEXTS.HIGHEST_BIDDER_RESERVE_MET}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderHighestBidderReserveNotMet = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='success'
                icon={<Header.IconSmile />}
                subText={HEADER_TEXTS.HIGHEST_BIDDER_RESERVE_NOT_MET_SUBTEXT}
                text={HEADER_TEXTS.HIGHEST_BIDDER_RESERVE_NOT_MET}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderCounterBid = () => {
    return (
      <Container
        ribbonColor='caution'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='caution'
                icon={<Header.IconExclamation />}
                subText={HEADER_TEXTS.COUNTERBID_SUBTEXT}
                text={HEADER_TEXTS.COUNTERBID}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderOutbidReserveMet = () => {
    return (
      <Container
        ribbonColor='caution'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='caution'
                icon={<Header.IconExclamation />}
                subText={HEADER_TEXTS.OUTBID_SUBTEXT}
                text={HEADER_TEXTS.OUTBID}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderOutbidReserveNotMet = () => {
    return (
      <Container
        ribbonColor='caution'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3')}>
              <Header
                color='caution'
                icon={<Header.IconExclamation />}
                subText={HEADER_TEXTS.OUTBID_SUBTEXT}
                text={HEADER_TEXTS.OUTBID}
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderBidsAway = () => {
    return (
      <Container
        ribbonColor='success'
        left={
          <>
            {renderTopDuringAuction()}
            <div className={cx('u-mb-3', 'one-away-header')}>
              <Header
                color='success'
                icon={<Header.IconSmile />}
                // TODO: do we need subtext here and what should it be ?
                text={
                  shouldShowOneAwayMessage
                    ? HEADER_TEXTS.ONE_AWAY
                    : HEADER_TEXTS.TWO_AWAY
                }
              />
              <PopupTooltip
                offset={-1}
                horizontalOffset={undefined}
                verticalOffset={undefined}
                position='top center'
                trigger={
                  <Icon
                    data-elm-id='one-away-info-icon'
                    className={cx('tooltip-icon')}
                    iconStyle='regular'
                    name='info-circle'
                    color='grayMedium'
                    fitted
                  />
                }
                content={
                  <Text
                    size='tiny'
                    color='white'
                    data-elm-id='one_away_tooltip_content'
                  >
                    <p>
                      {shouldShowOneAwayMessage
                        ? 'Auction is one bid away from reserve met. Subject to seller acceptance.'
                        : 'Auction bidding is near the reserve amount.  Subject to seller acceptance.'}
                    </p>
                  </Text>
                }
              />
            </div>
            {renderBidDetails()}
            <FAQSection />
          </>
        }
        right={renderSecond()}
        rightRibbon={renderRightRibbon()}
      />
    )
  }

  const renderContent = () => {
    if (
      (shouldShowOneAwayMessage || shouldShowTwoAwayMessage) &&
      segment.reserve_status === graph.enums.ReserveStatus.ReserveNotMet
    ) {
      return renderBidsAway()
    }
    switch (getBidBannerState(segment, listing)) {
      case BID_BANNER_STATES.HIGHEST_BIDDER_RESERVE_MET:
        return renderHighestBidderReserveMet()
      case BID_BANNER_STATES.HIGHEST_BIDDER_RESERVE_NOT_MET:
        return renderHighestBidderReserveNotMet()
      case BID_BANNER_STATES.COUNTERBID:
        return renderCounterBid()
      case BID_BANNER_STATES.OUTBID_RESERVE_MET:
        return renderOutbidReserveMet()
      case BID_BANNER_STATES.OUTBID_RESERVE_NOT_MET:
        return renderOutbidReserveNotMet()
      case BID_BANNER_STATES.STARTED:
      default:
        return renderNoBids()
    }
  }

  return (
    <PresenceContext.Consumer>
      {({
        updatePresenceContext,
      }: {
        updatePresenceContext: (args: any) => void
      }) => (
        <div
          onMouseEnter={() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            updatePresenceContext((ctx: any) => ({
              ...ctx,
              hovering_on_bid: true,
            }))
          }}
          onMouseLeave={() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            updatePresenceContext((ctx: any) => ({
              ...ctx,
              hovering_on_bid: false,
            }))
          }}
        >
          {renderContent()}
        </div>
      )}
    </PresenceContext.Consumer>
  )
}
